import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import type { DashboardPanel, DashboardPanelSeries } from './types';

const PANEL = {
  border: '1px solid #4f5255',
  borderRadius: 2,
  bgcolor: '#1f1f1f',
  p: 2,
};

const TIME_RANGES = [
  { label: '15 minutes', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: '6 hours', minutes: 360 },
  { label: '24 hours', minutes: 1440 },
];

const SERIES_COLORS = ['#8bc1f7', '#92c55e', '#f4c145', '#b2a3ff', '#f0561d'];

function formatUnitValue(value: number, unit: string): string {
  if (unit === 'bytes' || unit === 'bytes/s') {
    const suffix = unit === 'bytes/s' ? '/s' : '';
    const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
    let scaled = value;
    let index = 0;
    while (Math.abs(scaled) >= 1024 && index < units.length - 1) {
      scaled /= 1024;
      index += 1;
    }
    return `${scaled.toFixed(scaled < 10 && index > 0 ? 1 : 0)} ${
      units[index]
    }${suffix}`;
  }
  if (unit === 'cores') {
    return value < 1
      ? `${Math.round(value * 1000)}m`
      : `${value.toFixed(2)} cores`;
  }
  return value.toFixed(value < 10 ? 2 : 0);
}

function seriesPoints(series: DashboardPanelSeries): [number, number][] {
  return series.values
    .map(([timestamp, value]) => [timestamp, Number(value)] as [number, number])
    .filter(([, value]) => Number.isFinite(value));
}

/**
 * Compact hand-rolled SVG line chart, one line per pod. Mirrors the
 * PrometheusGraph rendering approach used for the PromQL panel in
 * OperationsCenter.tsx, adapted to the dashboard's per-pod series shape.
 */
function PodSeriesGraph({
  series,
  unit,
}: {
  series: DashboardPanelSeries[];
  unit: string;
}) {
  const lines = useMemo(() => {
    const all = series.flatMap(seriesPoints);
    if (!all.length) return [];
    const minX = Math.min(...all.map(point => point[0]));
    const maxX = Math.max(...all.map(point => point[0]));
    const minY = Math.min(0, ...all.map(point => point[1]));
    const maxY = Math.max(...all.map(point => point[1]));
    return series.map((entry, index) => {
      const points = seriesPoints(entry);
      const last = points[points.length - 1];
      return {
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        label: entry.pod || `Series ${index + 1}`,
        current: last ? formatUnitValue(last[1], unit) : undefined,
        path: points
          .map(
            ([x, y]) =>
              `${20 + ((x - minX) / Math.max(1, maxX - minX)) * 760},${
                180 - ((y - minY) / Math.max(1e-12, maxY - minY)) * 150
              }`,
          )
          .join(' '),
      };
    });
  }, [series, unit]);

  if (!lines.length) {
    return (
      <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
        No samples were returned for this panel.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <svg width="800" height="200" role="img" aria-label="Namespace metric">
          <line x1="20" y1="180" x2="780" y2="180" stroke="#4f5255" />
          <line x1="20" y1="30" x2="20" y2="180" stroke="#4f5255" />
          {lines.map(line => (
            <polyline
              key={line.label}
              points={line.path}
              fill="none"
              stroke={line.color}
              strokeWidth="2"
            />
          ))}
        </svg>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {lines.map(line => (
          <Chip
            key={line.label}
            size="small"
            label={
              line.current ? `${line.label} · ${line.current}` : line.label
            }
            sx={{ color: line.color, borderColor: line.color }}
            variant="outlined"
          />
        ))}
      </Stack>
    </>
  );
}

function DashboardPanelCard({ panel }: { panel: DashboardPanel }) {
  return (
    <Box sx={PANEL}>
      <Typography sx={{ color: '#f0f0f0', fontWeight: 700, mb: 1 }}>
        {panel.title}
      </Typography>
      <PodSeriesGraph series={panel.series} unit={panel.unit} />
    </Box>
  );
}

export function NamespaceDashboard({
  api,
  cluster,
  namespace,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
}) {
  const [minutes, setMinutes] = useState(60);
  const [panels, setPanels] = useState<DashboardPanel[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setUnavailable(null);
    try {
      const result = await api.namespaceDashboard(cluster, namespace, minutes);
      setPanels(result.panels);
    } catch (loadError) {
      const status = (loadError as { status?: number })?.status;
      const message =
        loadError instanceof Error ? loadError.message : String(loadError);
      if (status === 503 || status === 404) {
        setUnavailable(message);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cluster && namespace) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cluster, namespace, minutes]);

  return (
    <Stack spacing={2}>
      <Box sx={PANEL}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ color: '#f0f0f0', fontWeight: 700, flex: 1 }}>
            Namespace metrics
          </Typography>
          <TextField
            select
            size="small"
            label="Time range"
            value={minutes}
            onChange={event => setMinutes(Number(event.target.value))}
            sx={{ width: 160 }}
          >
            {TIME_RANGES.map(range => (
              <MenuItem key={range.minutes} value={range.minutes}>
                {range.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>
      {unavailable && (
        <Alert severity="info">
          Metrics aren't available for this namespace: {unavailable}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress size={24} />}
      {!loading && panels && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {panels.map(panel => (
            <DashboardPanelCard key={panel.id} panel={panel} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
